<template>
  <div
    class="flex h-[50px] items-center justify-between bg-[color:var(--color-header-top-bg)] px-5 text-sm text-[color:var(--color-header-top-text)] xl:px-12"
  >
    <div class="flex w-full items-center justify-between">
      <div class="flex">
        <div class="order-1 flex">
          <template v-for="(usp, index) in $cfg.uspsTopbar" :key="index">
            <div class="mr-2.5 flex items-center">
              <VcIcon class="mt-1 text-[--color-primary-500]" name="checkmark" :size="16" />
              <p class="text-sm">{{ usp.label }}</p>
            </div>
          </template>
        </div>

        <div class="order-1 mx-2 flex xl:mx-4">
          <SocialLinks small />
        </div>
      </div>

      <div class="flex items-center text-sm">
        <div v-if="$cfg.topbarItems" class="flex items-center">
          <template v-for="(item, index) in $cfg.topbarItems" :key="index">
            <a
              class="font-bold hover:text-[color:var(--color-primary-500)]"
              :href="getSafeUrl(item.url)"
              :target="item.newWindow ? '_blank' : ''"
            >
              {{ item.label }}
            </a>
            <div class="mx-4 hidden h-5 w-px bg-[color:var(--color-primary-500)] md:block"></div>
          </template>
        </div>

        <!-- Authorized menu items -->
        <template v-if="isAuthenticated">
          <TopHeaderLink to="/account/profile">
            {{ $t("shared.layout.header.top_header.link_dashboard") }}
          </TopHeaderLink>

          <span class="mx-4 h-5 w-px bg-[color:var(--color-primary-500)]" />

          <!-- Account menu -->
          <div ref="loginMenu" class="relative flex flex-row items-center gap-x-1">
            <!-- Operator -->
            <template v-if="operator">
              <span class="font-bold">{{ operator.contact?.fullName || operator.userName }}</span>
              <span v-t="'shared.layout.header.top_header.logged_in_as'" class="text-neutral-400" />
            </template>

            <button
              type="button"
              class="flex cursor-pointer items-center py-1 text-[color:var(--color-header-top-link)] hover:text-[color:var(--color-header-top-link-hover)]"
              @click="loginMenuVisible = !loginMenuVisible"
            >
              <span class="font-bold">{{ user.contact?.fullName || user.userName }}</span>

              <VcIcon
                class="ms-1.5 text-[--color-primary] [--vc-icon-size:1rem] lg:text-[--color-primary] lg:[--vc-icon-size:0.625rem]"
                :name="loginMenuVisible ? 'chevron-up' : 'chevron-down'"
              />
            </button>

            <div
              v-if="loginMenuVisible"
              class="absolute right-0 top-full z-10 flex w-60 flex-col space-y-3 rounded-md bg-white px-3 py-4 text-black shadow-md"
            >
              <div class="flex items-center justify-between">
                <router-link
                  to="/account/profile"
                  class="flex items-center hover:text-[color:var(--color-primary-500)]"
                  @click="loginMenuVisible = false"
                >
                  <VcIcon class="text-[--color-primary]" name="user-circle" />

                  <span class="ml-2">{{ user.contact?.fullName }}</span>
                </router-link>

                <div class="grow"></div>

                <VcButton
                  :title="$t('shared.layout.header.link_logout')"
                  class="ml-4 !border-neutral-200 !p-0 w-[30px]"
                  variant="outline"
                  color="neutral"
                  size="xs"
                  icon
                  @click="signOut"
                >
                  <VcIcon name="logout" :size="16" class="mt-0.5 text-neutral-400" />
                </VcButton>
              </div>
              <div
                v-if="(isAdministrator || canImpersonate || operator) && storeId !== 'veldkoeltechniek-intern'"
                class="flex w-full"
                @click="loginMenuVisible = false"
              >
                <router-link
                  :to="{ name: 'SignInAdministrator' }"
                  class="flex w-full items-center justify-between hover:text-[color:var(--color-primary-500)]"
                >
                  <span class="ml-2">{{ $t("shared.account.sign_in_form.impersonate.switch_user") }}</span>
                  <VcButton
                    class="ml-4 !p-0 !border-neutral-200 w-[32px]"
                    variant="outline"
                    color="neutral"
                    size="xs"
                    :title="$t('shared.layout.header.link_logout')"
                  >
                    <VcIcon class="mt-0.5 text-neutral-400" name="rotate" :size="16" />
                  </VcButton>
                </router-link>
              </div>
            </div>

            <div v-if="isMultiOrganization" class="border-t py-3">
              <div class="px-3 py-1 text-xs text-neutral-600">
                {{ $t("common.labels.organizations") }}
              </div>

              <VcRadioButton
                v-for="item in user.contact?.organizations?.items"
                :key="item.id"
                v-model="contactOrganizationId"
                :label="item.name"
                :value="item.id"
                class="flex px-3 py-1 text-sm"
                @change="selectOrganization"
              />
            </div>
          </div>
        </template>

        <!-- Unauthorized menu items -->
        <template v-else>
          <TopHeaderLink to="/sign-in">
            {{ $t("shared.layout.header.link_sign_in") }}
          </TopHeaderLink>

          <span v-if="anonymousUsersAllowed" class="mx-3 size-1 rounded-full bg-[color:var(--color-primary-500)]" />

          <TopHeaderLink v-if="anonymousUsersAllowed" to="/sign-up">
            {{ $t("shared.layout.header.link_register_now") }}
          </TopHeaderLink>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from "@vueuse/core";
import { ref } from "vue";
import { useSignMeOut, useUser } from "@/shared/account";
import { SocialLinks } from "@/shared/layout";
import TopHeaderLink from "../_internal/top-header-link.vue";
import { VirtoPermissions } from "@/core/enums";
import { globals } from "@/core/globals";
import { getSafeUrl } from "@/core/utilities";

const {
  isAuthenticated,
  anonymousUsersAllowed,
  isMultiOrganization,
  user,
  operator,
  isAdministrator,
  isServiceEmployee,
  checkPermissions,
  switchOrganization,
} = useUser();

const { signMeOut } = useSignMeOut();

const loginMenu = ref(null);
const loginMenuVisible = ref(false);
const { storeId } = globals;

const contactOrganizationId = ref(user.value?.contact?.organizationId);
const canImpersonate = checkPermissions(VirtoPermissions.LoginOnBehalf);

async function selectOrganization(): Promise<void> {
  if (!contactOrganizationId.value) {
    return;
  }

  await switchOrganization(contactOrganizationId.value);
  loginMenuVisible.value = false;
}

async function signOut() {
  await signMeOut();
  location.href = "/";
}

onClickOutside(loginMenu, () => {
  loginMenuVisible.value = false;
});
</script>
